import { useState, useEffect, useRef } from 'react'
import { IconX, IconSearch } from '@tabler/icons-react'
//@ts-ignore
import { Modal, ModalBody } from 'reactstrap'
//@ts-ignore
import { InputGroup, Avatar } from 'dsm-react-lib'
import { useChatService } from '../services/chatService'
import { highlightSearchTerm, boldSearchTerm, checkIsDrawerMode } from '../utils/helpers'
import { useSelector } from 'react-redux'
import { getInitials } from '../../../../utility/Utils'
import { useNavigate } from 'react-router-dom'
import { useSasToken } from '../../../../_hooks/useSasToken'
import { staticAssetsPath } from '../../../../configs/constants'

interface ChatSearchResult {
  threadId: string
  threadName: string
  snippet: string
  lastActivity: string
  agentId?: string
}

interface ChatSearchModalProps {
  isOpen: boolean
  toggle: () => void
  onSelectChat: (chatId: string, title: string) => void
  recentChats?: {
    chatId?: string
    title?: string
    isGroup?: boolean
    groupName?: string
    agentId?: string
  }[]
}

export const formatLastActivity = (isoDate: string): string => {
  const date = new Date(isoDate)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

const ChatSearchModal: React.FC<ChatSearchModalProps> = ({
  isOpen,
  toggle,
  onSelectChat,
  recentChats = []
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [results, setResults] = useState<ChatSearchResult[]>([])
  const [resultMessage, setResultMessage] = useState('')
  const [hasSearched, setHasSearched] = useState(false)
  const { searchThreads, isSearchingThreads } = useChatService()
  const currentQueryRef = useRef('')
  const searchThreadsRef = useRef(searchThreads)
  useEffect(() => {
    searchThreadsRef.current = searchThreads
  }, [searchThreads])
  const { appendToken } = useSasToken()
  const chatbotIcon = appendToken(`${staticAssetsPath}/images/logo/ALI_Chatbot-vb.png`)
  const isDrawerMode = checkIsDrawerMode()
  const navigate = useNavigate()
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const agentList = useSelector((state: any) => state.agentReducer.agentsList)

  const handleClose = () => {
    setSearchQuery('')
    currentQueryRef.current = ''
    setResults([])
    setResultMessage('')
    setHasSearched(false)
    toggle()
  }

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const query = searchQuery.trim()
    currentQueryRef.current = query

    debounceRef.current = setTimeout(async () => {
      if (!query) {
        setResults([])
        setResultMessage('')
        setHasSearched(false)
        return
      }
      setHasSearched(true)
      const response = await searchThreadsRef.current(query)
      if (currentQueryRef.current === query) {
        if (response?.results) {
          setResults(response.results)
          setResultMessage(response.message || '')
        } else {
          setResults([])
          setResultMessage('No results found.')
        }
      }
    }, 400)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [searchQuery])

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        const input = document.querySelector('.chat-search-input input') as HTMLInputElement
        if (input) input.focus()
      }, 150)
    }
  }, [isOpen])

  const handleSelectResult = (result: ChatSearchResult) => {
    onSelectChat(result.threadId, result.threadName)
    if (!isDrawerMode) navigate(`/chat/${result.threadId}`)
    handleClose()
  }

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="lg" className="p-1">
      <div className="modal-header !bg-[var(--surface-bg)] !justify-between !flex !items-center mb-0 rounded-t border border-[var(--theme-border)]">
        <div className="flex items-center w-full relative">
          <InputGroup
            addon={<IconSearch size={20} color="var(--color-primary)" />}
            addonPosition="start"
            addonType="merged"
            className="chat-search-input"
            placeholder="Search chats..."
            value={searchQuery}
            onChange={(e: any) => setSearchQuery(e.target.value.trimStart())}
            ref={inputRef}
          />
          <IconX
            size={20}
            onClick={() => setSearchQuery('')}
            className="text-[var(--text-body)] absolute right-4 top-[20%] -translate-y-1/2 z-10 transition-colors duration-300 ease-in-out hover:text-[var(--text-heading)] cursor-pointer"
          />
        </div>
      </div>

      <ModalBody
        className={`p-0 bg-[var(--surface-bg)] border-t border-[var(--theme-border)] h-[70vh] !max-h-[600px] overflow-y-auto scrollbar-thin scrollbar-thumb-[var(--color-primary)] scrollbar-track-transparent scrollbar-thumb-rounded-full chat-primary`}>
        {isSearchingThreads ? (
          <ul className="mt-3 p-0 list-none">
            {Array.from({ length: 5 }).map((_, i) => (
              <li
                key={i}
                className="flex items-center gap-3 px-[14px] py-[10px] border-b border-[var(--theme-border)]">
                <div className="flex-shrink-0 w-[35px] h-[35px] rounded-full theme-surface-muted animate-pulse" />
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-center justify-between gap-4">
                    <div className="h-[13px] w-2/5 rounded theme-surface-muted animate-pulse" />
                    <div className="h-[11px] w-[60px] rounded theme-surface-muted animate-pulse" />
                  </div>
                  <div className="h-[12px] w-full rounded theme-surface-muted animate-pulse" />
                  <div className="h-[12px] w-4/5 rounded theme-surface-muted animate-pulse" />
                </div>
              </li>
            ))}
          </ul>
        ) : hasSearched && results.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-[var(--icon-muted)]">
            <IconSearch size={36} className="mb-2 opacity-40" />
            <p className="text-sm">No chats found for &quot;{searchQuery}&quot;</p>
          </div>
        ) : results.length > 0 ? (
          <div className="py-1">
            {resultMessage && (
              <p className="section-label px-[14px] py-[6px] bg-[var(--surface-muted)] border-b border-[var(--theme-border)]">
                {resultMessage}
              </p>
            )}
            <ul className="divide-y divide-[var(--theme-border)] m-0 p-0 list-none">
              {results.map((result) => (
                <li
                  key={result.threadId}
                  className="flex items-center gap-2 px-[14px] py-[10px] cursor-pointer hover:bg-[var(--item-hover-bg)] transition-colors duration-150"
                  onClick={() => handleSelectResult(result)}>
                  <div className="flex-shrink-0 flex items-center justify-center">
                    {agentList?.find((agent: any) => agent?.id === result.agentId) ? (
                      agentList?.find((agent: any) => agent?.id === result.agentId)?.avatarUrl ? (
                        <Avatar
                          size="34px"
                          content={
                            agentList.find((agent: any) => agent?.id === result.agentId)?.avatarUrl
                          }
                          className="shrink-0 !m-0"
                        />
                      ) : (
                        <Avatar
                          size="38px"
                          content={getInitials(
                            agentList.find((agent: any) => agent?.id === result.agentId)?.name
                          )}
                          className="shrink-0 !m-0"
                        />
                      )
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[var(--surface-bg)] flex items-center justify-center">
                        <Avatar size="54px" content={chatbotIcon} className="shrink-0 !m-0" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="caption-text font-semibold text-[var(--text-heading)] truncate">
                        {boldSearchTerm(result.threadName, searchQuery)}
                      </span>
                      <span className="caption-text text-[var(--text-subheading)] whitespace-nowrap flex-shrink-0 flex items-center">
                        {formatLastActivity(result.lastActivity)}
                      </span>
                    </div>
                    <p className="caption-text text-[var(--text-body)] mt-[2px] line-clamp-2 break-words">
                      {boldSearchTerm(result.snippet, searchQuery)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : !searchQuery && recentChats.length > 0 ? (
          <div className="py-1">
            <ul className="m-0 p-0 list-none">
              {recentChats.map((chat, i) =>
                chat.isGroup ? (
                  <li
                    key={`group-${i}`}
                    className="section-label text-xs px-[14px] py-[6px] bg-[var(--surface-muted)] border-b border-[var(--theme-border)] !text-[var(--text-subheading)]">
                    {chat.groupName}
                  </li>
                ) : (
                  <li
                    key={chat.chatId}
                    className="flex items-center gap-2 px-[14px] py-[10px] cursor-pointer hover:bg-[var(--item-hover-bg)] transition-colors duration-150 border-b border-[var(--theme-border)]"
                    onClick={() => {
                      onSelectChat(chat.chatId!, chat.title!)
                      if (!isDrawerMode) navigate(`/chat/${chat.chatId}`)
                      handleClose()
                    }}>
                    <div className="flex-shrink-0 flex items-center justify-center">
                      {agentList.find((agent: any) => agent?.id === chat?.agentId) ? (
                        agentList.find((agent: any) => agent?.id === chat?.agentId)?.avatarUrl ? (
                          <Avatar
                            size="34px"
                            content={
                              agentList.find((agent: any) => agent?.id === chat?.agentId)?.avatarUrl
                            }
                            className="shrink-0 !m-0"
                          />
                        ) : (
                          <Avatar
                            size="38px"
                            content={getInitials(
                              agentList.find((agent: any) => agent?.id === chat?.agentId)?.name
                            )}
                            className="shrink-0 !m-0"
                          />
                        )
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[var(--surface-bg)] flex items-center justify-center">
                          <Avatar size="54px" content={chatbotIcon} className="shrink-0 !m-0" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="!text-sm font-medium text-[var(--text-heading)] truncate block">
                        {chat.title || 'New chat'}
                      </span>
                    </div>
                  </li>
                )
              )}
            </ul>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-10 text-[var(--icon-muted)]">
            <IconSearch size={36} className="mb-2 opacity-40" />
            <p className="text-sm">Type to search your chat history</p>
          </div>
        )}
      </ModalBody>
    </Modal>
  )
}

export default ChatSearchModal
